import { css } from '@emotion/react'
import { ActionCards } from 'components/actions/ActionCards'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { HelpCarousel } from 'components/content/helpCarousel/HelpCarousel'
import { MediaMentionsRow } from 'components/content/mediaMentions/MediaMentionsRow'
import { DirectiveSteppedGuide } from 'components/directive/directiveSteppedGuide'
import { PetHeroImage } from 'components/directive/pet/petHeroImage/PetHeroImage'
import { getDirectiveSpec } from 'components/directive/utils/directiveSpecs'
import {
  useCreateDirectiveLogic,
  useDirectiveLPActions,
} from 'components/directive/utils/directiveUtils'
import { DirectiveLPPageProps } from 'components/directive/utils/types'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { ContentfulImage } from 'components/Image'
import { LandingPageTextImageMediaMentions } from 'components/landingPageTextImageSection/LandingPageTextImageMediaMentions'
import { LandingPageTextImagePerks } from 'components/landingPageTextImageSection/LandingPageTextImagePerks'
import { Layout } from 'components/layout/Layout'
import { LandingPageHero } from 'components/landingPageHero/LandingPageHero'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { SecuritySection } from 'components/security/SecuritySection'
import { Signup, ToneContext } from 'components/signup/Signup'
import { Spacer } from 'components/spacer/Spacer'
import { Statistics } from 'components/statistics/Statistics'
import { Title_h3_default } from 'components/Typography'
import { useTranslation } from 'next-i18next'
import React, { useMemo } from 'react'
import { colors } from 'utils/colors'
import { useContentfulImages } from 'utils/contentful/ContentfulImagesContext'
import { numberRange } from 'utils/general'
import { useResponsiveLocalization } from 'utils/hooks'
import { mapKeysToTrans } from 'utils/i18n/mapKeysToTrans'
import { BREAKPOINTS } from 'utils/styled'
import { faqTitleCss, SHeroImageContainer } from './LegacyDirectiveLandingPage.styled'

export const LegacyDirectiveLandingPage = ({
  directiveType,
  mediaMentions,
  faq,
}: DirectiveLPPageProps) => {
  const images = useContentfulImages()
  const spec = useMemo(() => getDirectiveSpec(directiveType), [directiveType])
  const { landingPageNamespace, LandingPageSections } = spec
  const { t } = useTranslation(landingPageNamespace)
  const { t: tCommon } = useTranslation('directive_lp_common')
  const responsiveLocalization = useResponsiveLocalization()

  const { cta, create, isCreating } = useCreateDirectiveLogic(directiveType)

  const actions = useDirectiveLPActions(directiveType)

  const signupPerks = mapKeysToTrans(
    tCommon,
    numberRange(4).map((i) => `directive_lp_common.section_signup.perk_${i}` as const)
  )

  const heroPerks = mapKeysToTrans(
    t,
    numberRange(4).map((i) => `${landingPageNamespace}.hero.perk_${i}` as const)
  )

  return (
    <Layout
      header={<TopNavigationLandingPage variant="estate-planning" />}
      footer={<Footer variant="estate-planning" />}
    >
      <PageTitle title={t(`${landingPageNamespace}.meta_title`)} />
      <PageMeta
        image={spec.openGraphImage}
        description={t(`${landingPageNamespace}.meta_description`)}
      />

      <Spacer size={[0, 3]} />

      <LandingPageHero
        caption={t(`${landingPageNamespace}.hero.caption`)}
        title={t(`${landingPageNamespace}.hero.title`)}
        overflowing
        image={
          <SHeroImageContainer>
            <ContentfulImage {...images[`${spec.typeLowercase}_hero`]} />
            {directiveType === 'PET' && <PetHeroImage />}
          </SHeroImageContainer>
        }
      >
        <LandingPageTextImagePerks iconColor={colors.orange[200]} perks={heroPerks} />
        <Spacer size={[1, 1.5]} />
        <Button
          type="button"
          tone="accent"
          size={['medium', 'large']}
          loading={isCreating}
          onClick={create}
        >
          {cta}
        </Button>
        <Spacer size={[0, 1.5]} />
        <LandingPageTextImageMediaMentions
          title={tCommon('directive_lp_common.hero.mentions_caption')}
          mentions={mediaMentions}
          responsiveItemCounts={[5, 5, 5]}
          className="desktop-only"
        />
      </LandingPageHero>

      <Spacer size={[2, 0]} />

      <Block className="mobile-only">
        <MediaMentionsRow
          title={tCommon('directive_lp_common.hero.mentions_caption')}
          mediaMentions={mediaMentions}
          hideQuotes
          noTitle
          limit={5}
        />
      </Block>

      <Spacer size={[3, 5]} />

      <Block>
        <ActionCards
          variant="round-button"
          title={tCommon('directive_lp_common.action_cards_title', responsiveLocalization)}
          actions={actions}
          scrollability="desktop-only"
          scrollerCss={css`
            padding-left: 0;
            padding-right: 0;
          `}
          css={css`
            @media ${BREAKPOINTS.MD.min} {
              grid-area: center / center / center / right;
            }
          `}
        />
      </Block>

      <Spacer size={[3, 5]} />

      <Statistics
        title={t(`${landingPageNamespace}.statistics.title`)}
        items={numberRange(3).map((i) => ({
          title: t(`${landingPageNamespace}.statistics.item_${i}.title`),
          description: t(`${landingPageNamespace}.statistics.item_${i}.desc`),
          icon: spec.statisticIcons[i],
        }))}
      />
      <Spacer size={[3, 6]} />

      <Block>
        <DirectiveSteppedGuide type={directiveType} />
      </Block>

      <Spacer size={[3, 7]} />

      <LandingPageSections />

      <FaqTwoColumns
        content={faq}
        title={t(`${landingPageNamespace}.faq_title`)}
        titleVariant={Title_h3_default}
        titleCss={faqTitleCss}
      />

      <Spacer size={[3, 7]} />

      <HelpCarousel />

      <Spacer size={[3, 7]} />

      <SecuritySection imagePlacement="right" />

      <Spacer size={[3, 7]} />

      <ToneContext.Provider value="accent">
        <Signup
          caption={tCommon('directive_lp_common.section_signup.caption')}
          title={tCommon('directive_lp_common.section_signup.title')}
          perks={signupPerks}
        />
      </ToneContext.Provider>
    </Layout>
  )
}
